import { useState, useEffect, useCallback } from 'react'
import { Package, RefreshCw, Search, ExternalLink } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'semiconductor', label: 'Semiconductor' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'logistics', label: 'Logistics' },
  { value: 'materials', label: 'Materials' },
  { value: 'energy', label: 'Energy' },
]

const CATEGORY_BADGE: Record<string, string> = {
  semiconductor: 'badge-purple',
  shipping: 'badge-blue',
  logistics: 'badge-cyan',
  materials: 'badge-amber',
  energy: 'badge-red',
}

const RISK_BADGE: Record<string, string> = {
  high: 'badge-red',
  medium: 'badge-amber',
  low: 'badge-green',
}

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function SupplyChainPage() {
  const [items, setItems] = useState<any[]>([])
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const params: Record<string, any> = { limit: 50 }
      if (categoryFilter !== 'all') params.category = categoryFilter
      const { data } = await api.get('/api/supply-chain/feed', { params })
      setItems(data.items || [])
    } finally {
      setLoading(false)
    }
  }, [categoryFilter])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const filtered = items.filter(item => {
    if (searchTerm) {
      const q = searchTerm.toLowerCase()
      if (
        !(item.title || '').toLowerCase().includes(q) &&
        !(item.summary || '').toLowerCase().includes(q)
      ) return false
    }
    return true
  })

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Package size={24} className="text-amber" /> Supply Chain Intelligence
          </h1>
          <p className="text-sm text-text-secondary mt-1">Disruption signals from FreightWaves, SEMI, Bloomberg Supply Chain, and logistics trackers</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search supply chain news..." className="input pl-9 py-2 text-xs" />
        </div>
        <select title="Filter by category" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="input w-auto py-2 text-xs">
          {CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {loading && !items.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Fetching supply chain feed...</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((item, idx) => (
          <div key={item.id || idx} className="card card-hover p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {item.category && (
                    <span className={clsx('text-[10px]', CATEGORY_BADGE[item.category] || 'badge-blue')}>{item.category}</span>
                  )}
                  {item.disruption_risk && (
                    <span className={clsx('text-[10px]', RISK_BADGE[item.disruption_risk] || 'badge-amber')}>Risk: {item.disruption_risk}</span>
                  )}
                </div>
                <h3 className="text-sm font-semibold text-text-primary leading-snug">
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener" className="hover:underline">{item.title}</a>
                  ) : item.title}
                </h3>
                <p className="text-xs text-text-secondary mt-1 leading-relaxed line-clamp-2">{item.summary}</p>
                <div className="flex items-center gap-2 mt-2 text-[10px] text-text-muted">
                  <span>{item.source}</span>
                  <span>&middot;</span>
                  <span>{relativeTime(item.timestamp)}</span>
                </div>
              </div>
              {item.url && (
                <a href={item.url} target="_blank" rel="noopener" className="text-text-muted hover:text-text-primary shrink-0">
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && (
          <p className="text-xs text-text-muted text-center py-8">No items match your current filters</p>
        )}
      </div>
    </div>
  )
}
